<template>
  <section>
    <div
      class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-5 tw:md:gap-8 tw:w-full tw:lg:max-w-(--tw-breakpoint-landing) tw:md:max-w-[638px] tw:py-6 tw:lg:px-0 tw:mx-auto tw:lg:py-8"
    >
      <div v-if="isVideoMode" class="tw:relative tw:w-[70%] tw:md:w-[600px] tw:lg:w-[700px] tw:mx-auto">
        <Videoplayer
          :src="RateConnectVideo"
          :isRounded="false"
          :controls="true"
          :muted="true"
          :autoplay="true"
          :playOnScroll="false"
          :data-testid="'rate-connect-video-landing'"
        />
        <button
          @click="handleCloseVideo"
          class="tw:absolute tw:left-[calc(100%+32px)] tw:top-0"
          :aria-label="$t('close')"
          :data-testid="'rate-connect-video-close-btn'"
        >
          <Close class="tw:size-3.5" />
        </button>
      </div>
      <div
        v-if="!isVideoMode || (screenWidth <= 768 && !isVideoMode)"
        class="tw:lg:w-1/2 tw:flex tw:flex-col tw:md:flex-row tw:lg:flex-col tw:items-center tw:lg:items-stretch tw:mx-2.5 tw:xs:mx-6 tw:md:mx-0"
      >
        <div class="tw:order-0 tw:md:order-1 tw:lg:order-0 tw:md:w-1/2 tw:lg:w-auto tw:md:ml-8 tw:lg:ml-0 tw:w-full">
          <img
            :src="RateConnectLogo"
            alt="Rate Connect"
            class="tw:w-[183px] tw:md:w-[203px] tw:lg:w-[280px] tw:mx-auto tw:md:mx-0"
          />
          <h3
            class="tw:font-bold tw:lg:text-[30px] tw:lg:leading-[40px] tw:md:text-2xl tw:text-xl tw:mt-2 tw:md:mt-4 tw:mb-4 tw:md:mb-0 tw:text-center tw:md:text-left"
            v-html="$t('tbs_mp_rc_header')"
          />
        </div>
        <div
          class="tw:relative tw:order-0 tw:lg:order-1 tw:rounded-xl tw:border tw:border-black line-shadow tw:lg:mt-6 tw:grow tw:md:w-1/2 tw:lg:w-auto tw:w-full tw:max-h-[170px] tw:md:max-h-full"
          :class="{ 'tw:cursor-pointer': language.includes('en') }"
          @click="handlePlayClick"
          data-testid="rate-connect-play-trigger"
        >
          <img
            :src="RateConnectImage"
            class="tw:rounded-lg tw:w-full tw:md:h-full tw:h-[170px] tw:object-cover"
            :class="{ 'tw:opacity-20': language.includes('en') }"
          />

          <img
            v-if="language.includes('en')"
            :src="PlayButton"
            :alt="$t('play')"
            class="tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:z-10 tw:transition-transform hover:tw:scale-110"
          />
        </div>
      </div>

      <!-- Web Version -->
      <div
        v-show="!isVideoMode"
        ref="containerRef"
        class="tw:lg:w-1/2 tw:hidden tw:md:flex tw:flex-wrap tw:gap-4 tw:lg:gap-6"
      >
        <div
          v-for="item in RATE_CONNECT_BENEFITS"
          :key="item.id"
          :ref="(el, index) => handleElementRef(el, index)"
          class="rc_item tw:lg:ml-[30px] tw:relative shadow tw:bg-white tw:last:mb-0 tw:rounded-xl tw:px-6 tw:py-5 tw:border tw:border-gray-300 tw:lg:pl-12 tw:items-stretch"
        >
          <div class="tw:h-full">
            <div class="tw:flex tw:items-center">
              <div
                class="tw:lg:absolute tw:bg-decorative-yellow tw:min-w-[45px] tw:h-[45px] tw:lg:h-[60px] tw:lg:w-[60px] tw:flex tw:justify-center tw:items-center tw:rounded-full icon tw:mr-4 tw:lg:mr-0"
              >
                <img :src="item.icon" :alt="$t(item.title)" />
              </div>
              <h2 class="tw:font-bold tw:lg:text-xl">{{ $t(item.title) }}</h2>
            </div>
            <p class="tw:text-sm tw:lg:text-base tw:mt-2 tw:lg:mt-0">{{ $t(item?.text) }}</p>
          </div>
        </div>
      </div>

      <!-- Mobile Version -->
      <div
        ref="containerRef"
        class="tw:md:hidden tw:flex tw:flex-col tw:md:flex-row tw:w-vw tw:overflow-hidden carousel-container tw:ml-2.5 tw:xs:ml-6"
      >
        <div class="inner rc_carousel-mobile" ref="inner" :style="innerStyles">
          <div
            ref="rcBoxesRef"
            v-for="item in RATE_CONNECT_BENEFITS"
            :key="item.id"
            :id="item.id"
            class="card shadow tw:bg-white tw:rounded-xl tw:px-6 tw:py-5 tw:border tw:border-gray-300"
          >
            <div class="tw:h-full">
              <div class="tw:flex tw:items-center">
                <div
                  class="tw:bg-decorative-yellow tw:min-w-[45px] tw:w-[45px] tw:h-[45px] tw:flex tw:justify-center tw:items-center tw:rounded-full tw:mr-4"
                >
                  <img :src="item.icon" :alt="$t(item.title)" />
                </div>
                <h2 class="tw:font-bold">{{ $t(item.title) }}</h2>
              </div>
              <p class="tw:text-sm tw:mt-2">{{ $t(item?.text) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick, onUpdated } from 'vue'

import TouchEvent from '@/utils/touchEvents.ts'
import Videoplayer from '@/components/studioPlus/Videoplayer.vue'
import Close from '@/components/icons/Close.svg?component'

import { useSlideInAnimation } from '@/composables/useScrollSlideIn.ts'
import { useIsMobile } from '@/composables/useIsMobile'
import { useSessionValues } from '@/composables/useSessionValues'

const { registerElement } = useSlideInAnimation({
  threshold: 0.3,
  staggerDelay: 500,
})

const RateConnectLogo = new URL('/src/assets/img/landing/landing_rate_connect.svg', import.meta.url)
const RateConnectImage = new URL('/src/assets/img/landing/landing_rate_connect_img.jpeg', import.meta.url)
const RateConnectVideo = new URL('/src/assets/videos/HomePageRateConnect.mp4', import.meta.url)

const BedIcon = new URL('/src/assets/img/landing/icons/bed_icon.svg', import.meta.url)
const EyeIcon = new URL('/src/assets/img/landing/icons/eye_icon.svg', import.meta.url)
const ThumbIcon = new URL('/src/assets/img/landing/icons/thumb_icon.svg', import.meta.url)
const BarsIcon = new URL('/src/assets/img/landing/icons/bars_icon.svg', import.meta.url)
const PlayButton = new URL('/src/assets/img/studio-plus/icons/PlayIcon.svg', import.meta.url)

const RATE_CONNECT_BENEFITS = [
  { id: 'slide_0', title: 'tbs_mp_rc_benefits_1', text: 'tbs_mp_rc_benefits_1_text', icon: BedIcon },
  { id: 'slide_1', title: 'tbs_mp_rc_benefits_2', text: 'tbs_mp_rc_benefits_2_text', icon: EyeIcon },
  { id: 'slide_2', title: 'tbs_mp_rc_benefits_3', text: 'tbs_mp_rc_benefits_3_text', icon: ThumbIcon },
  { id: 'slide_3', title: 'tbs_mp_rc_benefits_4', text: 'tbs_mp_rc_benefits_4_text', icon: BarsIcon },
]

const isVideoMode = ref(false)
const containerRef = ref(null)
const rcBoxesRef = ref([])
const { language } = useSessionValues()
const { screenWidth } = useIsMobile()

const touchEvent = ref(null)
const current = ref(1)
const timer = ref('')
const isAutoplayActive = ref(true)
const inner = ref(null)
const innerStyles = ref({})
const step = ref('')
const transitioning = ref(false)

const handleElementRef = (el, index) => {
  if (el) {
    rcBoxesRef.value[index] = el
    registerElement(el)
  }
}

const setEqualHeight = () => {
  nextTick(async () => {
    if (window.innerWidth > 767) {
      rcBoxesRef.value.forEach(box => (box.style.height = 'auto'))
      return
    }
    const boxes = rcBoxesRef.value
    if (!boxes || boxes.length === 0) return

    boxes.forEach(box => {
      box.style.height = 'auto'
    })

    await nextTick()

    let maxHeight = 0

    boxes.forEach(box => {
      const currentHeight = box.offsetHeight
      if (currentHeight > maxHeight) {
        maxHeight = currentHeight
      }
    })

    boxes.forEach(box => {
      box.style.height = maxHeight + 'px'
    })
  })
}

watch(language, () => {
  nextTick(() => setEqualHeight())
})

onUpdated(() => {
  setEqualHeight()
})

onMounted(async () => {
  await nextTick()
  setStep()
  autoplay()
  document.addEventListener('touchstart', event => {
    const carousel = containerRef.value?.querySelector('.rc_carousel-mobile')
    if (carousel && carousel.contains(event.target)) {
      touchEvent.value = new TouchEvent(event)
    } else {
      touchEvent.value = null
    }
  })

  document.addEventListener('touchend', handleSwipe)
  window.addEventListener('resize', setEqualHeight)
})

onBeforeUnmount(() => {
  clearTimeout(timer.value)
  window.removeEventListener('resize', setEqualHeight)
  document.removeEventListener('touchend', handleSwipe)
})

const afterTransition = callback => {
  const listener = () => {
    callback()
    inner.value?.removeEventListener('transitionend', listener)
  }
  inner.value?.addEventListener('transitionend', listener)
}

const resetTranslate = () => {
  innerStyles.value = {
    transition: 'none',
    transform: 'translateX(0)',
  }
}

const setStep = () => {
  const innerWidth = inner.value.scrollWidth
  const totalCards = RATE_CONNECT_BENEFITS.length

  step.value = innerWidth / totalCards
}

const moveLeft = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(-${step.value}px)`,
  }
}

const moveRight = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(${step.value}px)`,
  }
}

const next = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveLeft()
  afterTransition(() => {
    const firstElement = containerRef.value.querySelector('.rc_carousel-mobile')?.firstElementChild
    containerRef.value.querySelector('.rc_carousel-mobile').appendChild(firstElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const prev = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveRight()
  afterTransition(() => {
    const lastElement = containerRef.value.querySelector('.rc_carousel-mobile').lastElementChild
    containerRef.value.querySelector('.rc_carousel-mobile').prepend(lastElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const updateCurrent = () => {
  const firstElementId = containerRef.value.querySelector('.rc_carousel-mobile').firstElementChild.id
  current.value = Number(firstElementId.split('_')[1])
}

const autoplay = () => {
  if (!isAutoplayActive.value) return
  next()
  timer.value = setTimeout(autoplay, 6000)
}

const handleSwipe = event => {
  if (!touchEvent.value) {
    return
  }
  isAutoplayActive.value = false
  clearTimeout(timer.value)
  touchEvent.value.setEndEvent(event)

  if (touchEvent.value.isSwipeRight()) {
    prev()
  } else if (touchEvent.value.isSwipeLeft()) {
    next()
  }
  touchEvent.value = null
}

const handlePlayClick = () => {
  if (language.value.includes('en')) {
    isVideoMode.value = true
  }
}
const handleCloseVideo = () => {
  isVideoMode.value = false
}
</script>
<style scoped>
.line-shadow {
  box-shadow: 3px 3px 0px 0px #000;
}
.shadow {
  box-shadow: 2px 2px 4px 0px #00000040;
}
.icon {
  @media (min-width: 1024px) {
    left: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
}
.image-column {
  flex: 0 0 auto;
}
.rc_item {
  flex: calc(50% - 8px) 0 0;
  @media (min-width: 1024px) {
    flex: calc(100% - 30px) 0 0;
  }
}

.rc_carousel-mobile {
  padding-bottom: 2px;
  display: flex;
  margin: auto;
  white-space: nowrap;
  transition: transform 1s;
  @media (max-width: 768px) {
    width: 100%;
  }
}

.card {
  background-color: var(--tw-color-white);
  min-width: 300px;
  min-height: 100%;
  position: relative;
  width: 330px;
  margin-right: 18px;
  display: inline-flex;
  white-space: wrap;
}
</style>
